import React, { useCallback, useState, useMemo, useEffect } from 'react';
import cn from 'classnames';
import Icon from 'components/UI/Icon';
import LazyImage from 'components/UI/LazyImage';
import CheckMark from 'components/UI/CheckMark';
import RadioMark from 'components/UI/RadioMark';
import { useTrans } from 'utils/localisation';

import * as styles from './SelectList.module.scss';

export default function SelectList({
  options,
  flags,
  selected,
  className,
  withSearch,
  type,
  onSelect,
  clear,
  title,
  showCount = 5,
}) {
  const t = useTrans(['filters', 'common']);
  const [search, setSearch] = useState('');
  const [showSearch, setShowSearch] = useState(withSearch);
  const [searchFocused, setSearchFocused] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const handleSearch = useCallback(e => setSearch(e.target.value), []);
  const clearSearch = useCallback(() => setSearch(''), []);

  const handleItemClick = clicked => {
    const result = selected.some(({ value }) => value === clicked.value)
      ? selected.filter(item => item.value !== clicked.value)
      : [...selected, clicked];
    onSelect(result);
  };

  const handleShowMore = () => {
    setShowMore(!showMore);
  };

  const optionsReady = useMemo(
    () =>
      options
        .map(item => {
          if (item.value) {
            return item;
          }

          return {
            value: item,
            label: item,
          };
        })
        .filter(item => {
          const getNormalizedValue = value =>
            typeof value === 'string' ? value.toLowerCase() : value.toString();

          return getNormalizedValue(item.label).includes(getNormalizedValue(search));
        }),
    [options, search],
  );

  useEffect(() => {
    if (optionsReady.length <= 8 && !search) {
      setShowSearch(false);
    } else {
      setShowSearch(true);
    }
  }, [optionsReady.length, search]);

  return (
    <div className={className}>
      <div className={styles.titleRow}>
        <p className={styles.title}>
          {title}
          {selected.length > 0 ? ` (${selected.length})` : null}
        </p>
        <button
          className={cn(styles.clearButton, !selected?.length && styles.disabled)}
          onClick={clear}
        >
          {t('clear')}
        </button>
      </div>
      <div className={styles.select}>
        {withSearch && showSearch && (
          <div className={cn(styles.searchRow, searchFocused && styles.focused)}>
            <Icon name="loupe-left-side" className={styles.searchIcon} />
            <input
              type="text"
              placeholder={t('search')}
              className={styles.searchInput}
              value={search}
              onChange={handleSearch}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
            />
            {search.length > 0 && (
              <Icon name="close" className={styles.closeIcon} onClick={clearSearch} />
            )}
          </div>
        )}
        {!optionsReady.length ? (
          // right now its hardcoded for location filters, will need refactoring if there will be more filters with search
          <div className={styles.noResult}>{t('noMatchingCountry')}</div>
        ) : (
          <ul>
            {optionsReady.map((item, i) => (
              <li key={item.value}>
                <div
                  role="button"
                  className={cn(
                    styles.itemButtonMultiple,
                    i >= showCount && !showMore && styles.hidden,
                  )}
                  onClick={() => handleItemClick(item)}
                >
                  {type === 'radio' ? (
                    <RadioMark
                      className={cn(styles.mark, styles.radioMark)}
                      checked={selected.some(({ value }) => value === item.value)}
                    />
                  ) : (
                    <CheckMark
                      className={cn(styles.mark, styles.checkMark)}
                      checked={selected.some(({ value }) => value === item.value)}
                    />
                  )}
                  {flags?.[item.label] && (
                    <LazyImage src={flags[item.label]} alt={item.label} className={styles.flag} />
                  )}
                  {item.label}
                </div>
              </li>
            ))}
          </ul>
        )}
        {optionsReady.length > showCount && (
          <div className={styles.buttonRow}>
            <button
              onClick={handleShowMore}
              className={cn(styles.showMore, showMore && styles.active)}
            >
              {!showMore ? t('showMore') : t('showLess')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
